/* /api/field-audits/* - 15-api-contract.md section 3.15: the caller sends
 * trees to themselves, and a tree somebody holds is skipped, with who; the
 * phone reads the trees they hold; a tree is released.
 * No limit on a send's size here: the route's refusal says it (refusal.ts),
 * so the number lives in one place.
 */
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { postJson, useAllPages } from "./envelope";
import type { AssignmentRecord, SendResult } from "./types";

const OPEN_LIST = ["field-audits", "assignments"] as const;

/* Every read that shows a tree's audit state: the list, its counts, each
 * tree's detail, and the open list the phone reads. */
function changedHolds(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["mirror", "trees"] }),
    queryClient.invalidateQueries({ queryKey: ["mirror", "tree-facets"] }),
    queryClient.invalidateQueries({ queryKey: ["mirror", "tree"] }),
    queryClient.invalidateQueries({ queryKey: OPEN_LIST }),
  ]);
}

export function useSendForAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (treeIds: string[]) =>
      postJson<SendResult>(
        "/api/field-audits/send",
        { tree_ids: treeIds },
        (status) => `the trees could not be sent for audit (${status})`,
      ),
    onSuccess: () => changedHolds(queryClient),
  });
}

/* The caller's open assignments, oldest first: what the phone pulls (`27`
 * section 8). Every page, so the phone never shows part of what it holds as
 * though it were all of it. */
export function useOpenAssignments() {
  return useAllPages<AssignmentRecord>(OPEN_LIST, "/api/field-audits/assignments", {});
}

/* Releases an assignment by its id, for its holder or an administrator; the
 * route decides which may (15 section 3.15). */
export function useReleaseAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      postJson<AssignmentRecord>(
        `/api/field-audits/assignments/${encodeURIComponent(assignmentId)}/release`,
        {},
        (status) => `the tree could not be released (${status})`,
      ),
    onSuccess: () => changedHolds(queryClient),
  });
}
